import { useState, type FormEvent } from "react";
import { categories, siteMeta } from "@/mocks/gossip";

const FORM_URL = "https://readdy.ai/api/form/daij83jp14a1h3mr755g";
const CONTENT_LIMIT = 500;

type Status = "idle" | "sending" | "success" | "error";

export default function ContactSection() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [contentLength, setContentLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      setContentLength(0);
      return;
    }
    formData.delete("website_alt");

    setStatus("sending");
    setFormError("");

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (typeof value === "string" && value.trim() !== "") {
          params.append(key, value);
        }
      });

      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMessage = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = /spam/i.test(String(serverMessage ?? ""));

      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(String(serverMessage || "提交失败，请稍后再试。"));
        return;
      }

      setStatus("success");
      form.reset();
      setContentLength(0);
    } catch {
      setStatus("error");
      setFormError("网络异常，提交没有成功，请稍后再试。");
    }
  };

  return (
    <section id="contact" className="bg-background-100 py-16 md:py-24">
      <div className="shell">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[0.95fr_1.05fr]">
          <div className="rounded-lg bg-primary-950 p-7 text-background-100 md:p-9">
            <span className="text-xs font-medium uppercase tracking-[0.2em] text-accent-300">Tip Us</span>
            <h2 className="mt-2 font-heading text-3xl text-background-50 md:text-4xl">爆料投稿 · 联系我们</h2>
            <p className="mt-4 text-sm leading-7 text-background-100/80">
              你手上的一条线索，可能正好是大家都在等的那块拼图。把事件的时间、地点和你能提供的佐证类型写清楚，编辑会尽快跟进核实。
            </p>

            <a
              href={`mailto:${siteMeta.email}`}
              className="mt-6 inline-flex cursor-pointer items-center gap-2 rounded-md border border-background-50/25 px-4 py-3 text-sm text-background-50 transition-colors hover:bg-background-50/10"
            >
              <i className="ri-mail-line" aria-hidden="true" />
              {siteMeta.email}
            </a>

            <ul className="mt-8 space-y-4 border-t border-background-50/15 pt-6">
              <li className="flex items-start gap-3">
                <i className="ri-time-line mt-0.5 text-lg text-accent-300" aria-hidden="true" />
                <p className="text-xs leading-6 text-background-100/80">请尽量提供准确的事件发生时间，便于我们按日历归档。</p>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-attachment-2 mt-0.5 text-lg text-accent-300" aria-hidden="true" />
                <p className="text-xs leading-6 text-background-100/80">截图、录屏等佐证请先自行打码，勿在投稿中暴露他人隐私。</p>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-forbid-2-line mt-0.5 text-lg text-accent-300" aria-hidden="true" />
                <p className="text-xs leading-6 text-background-100/80">请勿提交恶意编造或涉及人身攻击的内容，我们不做这类扩散。</p>
              </li>
            </ul>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            {status === "success" ? (
              <div className="flex h-full flex-col items-center justify-center py-10 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-3xl text-secondary-700">
                  <i className="ri-checkbox-circle-line" aria-hidden="true" />
                </span>
                <h3 className="mt-5 font-heading text-2xl text-foreground-950">投稿已收到</h3>
                <p className="mt-3 max-w-sm text-sm leading-7 text-foreground-600">
                  感谢你的线索。编辑会尽快核对，如果内容有进展，我们会在蹲点日历中补充后续。
                </p>
                <button type="button" onClick={() => setStatus("idle")} className="btn-primary mt-7 cursor-pointer">
                  <i className="ri-add-line" aria-hidden="true" />
                  再投一条
                </button>
              </div>
            ) : (
              <form id="tipping-form" data-readdy-form="tipping-form" onSubmit={handleSubmit} noValidate>
                <h3 className="font-heading text-xl text-foreground-950">填写投稿信息</h3>
                <p className="mt-1.5 text-xs text-foreground-500">带 * 的为必填项</p>

                <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="nickname" className="mb-1.5 block text-xs font-medium text-foreground-700">
                      你的称呼 *
                    </label>
                    <input
                      id="nickname"
                      name="nickname"
                      type="text"
                      required
                      placeholder="怎么称呼你"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="mb-1.5 block text-xs font-medium text-foreground-700">
                      联系邮箱 *
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder="用于向你确认线索"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="category" className="mb-1.5 block text-xs font-medium text-foreground-700">
                      爆料分类 *
                    </label>
                    <select
                      id="category"
                      name="category"
                      required
                      defaultValue=""
                      className="w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    >
                      <option value="" disabled>
                        请选择分类
                      </option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.name}>
                          {cat.name}
                        </option>
                      ))}
                      <option value="其他">其他</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="happened_at" className="mb-1.5 block text-xs font-medium text-foreground-700">
                      事件发生时间
                    </label>
                    <input
                      id="happened_at"
                      name="happened_at"
                      type="text"
                      placeholder="例如 9 月 12 日晚"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label htmlFor="content" className="mb-1.5 block text-xs font-medium text-foreground-700">
                    爆料内容 *
                  </label>
                  <textarea
                    id="content"
                    name="content"
                    required
                    rows={5}
                    maxLength={CONTENT_LIMIT}
                    onChange={(event) => setContentLength(event.target.value.length)}
                    placeholder="请描述发生了什么、在哪里、有哪些可以佐证的线索"
                    className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-6 text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  />
                  <p className="mt-1.5 text-right text-[11px] text-foreground-400">
                    {contentLength} / {CONTENT_LIMIT}
                  </p>
                </div>

                <input
                  className="aux-note-field"
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === "error" && formError && (
                  <p className="mt-4 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2.5 text-xs leading-6 text-primary-700">
                    <i className="ri-error-warning-line mt-0.5 text-base" aria-hidden="true" />
                    {formError}
                  </p>
                )}

                <button type="submit" disabled={status === "sending"} className="btn-primary mt-6 w-full disabled:cursor-not-allowed disabled:opacity-70">
                  {status === "sending" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" aria-hidden="true" />
                      正在提交
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line" aria-hidden="true" />
                      提交爆料
                    </>
                  )}
                </button>
                <p className="mt-3 text-center text-[11px] leading-5 text-foreground-400">
                  提交即表示你确认所填信息属实，并同意我们据此进行核实与后续跟进。
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}